// src/constants/reservationIssue.ts
var RESERVATION_ISSUE_CATEGORY = {
  /** Pre-start withdrawal after acknowledgement — auto-releases the assignment. */
  CANNOT_DRIVE: "cannot_drive",
  /** Passenger never appeared at/after the effective pickup — routes to the operator. */
  NO_SHOW: "no_show",
  /** Safety or incident report. */
  INCIDENT: "incident",
  /** General operational report. */
  OTHER: "other"
};
var RESERVATION_ISSUE_CATEGORIES = Object.values(
  RESERVATION_ISSUE_CATEGORY
);
var RESERVATION_ISSUE_SEVERITY = {
  URGENT: "urgent",
  HIGH: "high",
  NORMAL: "normal"
};
var RESERVATION_ISSUE_SEVERITIES = Object.values(
  RESERVATION_ISSUE_SEVERITY
);
var RESERVATION_ISSUE_STATUS = {
  OPEN: "open",
  ACKNOWLEDGED: "acknowledged",
  RESOLVED: "resolved"
};
var RESERVATION_ISSUE_STATUSES = Object.values(
  RESERVATION_ISSUE_STATUS
);
var RESERVATION_ISSUE_EVENT_TYPE = {
  REPORTED: "reported",
  ACKNOWLEDGED: "acknowledged",
  RESOLVED: "resolved"
};
var RESERVATION_ISSUE_EVENT_TYPES = Object.values(
  RESERVATION_ISSUE_EVENT_TYPE
);
var RESERVATION_ISSUE_REPORTER_ROLE = {
  ASSIGNED_DRIVER: "assigned_driver",
  SOLO_OWNER_DRIVER: "solo_owner_driver"
};
var RESERVATION_ISSUE_REPORTER_ROLES = Object.values(
  RESERVATION_ISSUE_REPORTER_ROLE
);
var RESERVATION_ISSUE_ACTOR_ROLE = {
  ASSIGNED_DRIVER: "assigned_driver",
  SOLO_OWNER_DRIVER: "solo_owner_driver",
  MANAGING_OPERATOR: "managing_operator"
};
var RESERVATION_ISSUE_ACTOR_ROLES = Object.values(
  RESERVATION_ISSUE_ACTOR_ROLE
);
var RESERVATION_ISSUE_OPERATION = {
  REPORT: "report",
  ACKNOWLEDGE: "acknowledge",
  RESOLVE: "resolve"
};
var RESERVATION_ISSUE_IDEMPOTENCY_KEY_MAX_LENGTH = 200;

// src/constants/index.ts
var PLACEHOLDER_DRIVER_SMS_CONSENT_VERSION = "2026-08-placeholder-v1";
var ADDRESS_SUGGESTION_TYPES = [
  "country",
  "region",
  "postcode",
  "district",
  "place",
  "city",
  "locality",
  "neighborhood",
  "street",
  "address",
  "poi",
  "category",
  "unknown"
];
var SUBSCRIPTION_STATUS = {
  ACTIVE: "active",
  TRIALING: "trialing",
  PAST_DUE: "past_due",
  CANCELLED: "cancelled",
  INACTIVE: "inactive"
};
var COMPANY_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended"
};
var COMPANY_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  DRIVER: "driver"
};
var PLATFORM_ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  SUPPORT: "support",
  VERIFIER: "verifier",
  DEVELOPER: "developer",
  READ_ONLY: "read_only"
};
var PLATFORM_PERMISSIONS = {
  VIEW_ALL_DATA: "view_all_data",
  EDIT_USERS: "edit_users",
  EDIT_COMPANIES: "edit_companies",
  VERIFY_COMPANIES: "verify_companies",
  EDIT_RESERVATIONS: "edit_reservations",
  MANAGE_SUBSCRIPTIONS: "manage_subscriptions",
  MANAGE_PLATFORM_ADMINS: "manage_platform_admins",
  VIEW_ANALYTICS: "view_analytics",
  ACCESS_LOGS: "access_logs"
};
var RESERVATION_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};
var PICKUP_MODE = {
  EXPLICIT: "explicit",
  FLIGHT_TRACKED: "flight_tracked"
};
var NOT_OVERDUE_ELIGIBLE_STATUSES = [
  RESERVATION_STATUS.IN_PROGRESS,
  RESERVATION_STATUS.COMPLETED,
  RESERVATION_STATUS.CANCELLED
];
var OVERDUE_ELIGIBLE_STATUSES = Object.values(
  RESERVATION_STATUS
).filter((status) => !NOT_OVERDUE_ELIGIBLE_STATUSES.includes(status));
var OVERDUE_ELIGIBLE_STATUS_SET = new Set(
  OVERDUE_ELIGIBLE_STATUSES
);
var OVERDUE_SCOPE_STATUS_PARAM = OVERDUE_ELIGIBLE_STATUSES.join(",");
var COMPANY_DECLINED_CANCELLATION_REASON = "company_declined";
var RESERVATION_CANCELLATION_OUTCOME = {
  CANCELLED: "cancelled",
  DECLINED: "declined"
};
var RESERVATION_STATUS_TRANSITIONS = {
  pending: ["confirmed", "cancelled"],
  confirmed: ["assigned", "cancelled"],
  assigned: ["in_progress", "completed", "confirmed", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: []
};
function canTransitionReservation(from, to) {
  return RESERVATION_STATUS_TRANSITIONS[from].includes(to);
}
function getReservationStage(input) {
  if (input.status === RESERVATION_STATUS.ASSIGNED) {
    return input.driverAcceptedAt ? "driver_confirmed" : "awaiting_driver";
  }
  return input.status;
}
var RESERVATION_STAGE_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  awaiting_driver: "Awaiting driver",
  driver_confirmed: "Driver confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled"
};
var COMPLETION_OUTCOME = {
  NORMAL: "normal",
  NO_SHOW: "no_show",
  INCIDENT: "incident"
};
var COMPLETION_OUTCOME_LABELS = {
  normal: "Completed normally",
  no_show: "Passenger no-show",
  incident: "Incident"
};
var PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PAID: "paid",
  PARTIAL: "partial"
};
var PAYMENT_METHODS = {
  CASH: "cash",
  VENMO: "venmo",
  ZELLE: "zelle",
  PAYPAL: "paypal",
  CHECK: "check",
  BANK_TRANSFER: "bank_transfer",
  CREDIT_CARD: "credit_card",
  OTHER: "other",
  /**
   * Online card payment through Stripe Connect (docs/stripe-connect-spec.md).
   * Written ONLY by the `payment_intent.succeeded` webhook handler — it is
   * deliberately absent from `paymentMethodSchema` so staff can never record
   * a manual payment as "stripe" (the webhook is the single writer).
   */
  STRIPE: "stripe"
};
var PAYMENT_REQUIREMENT = {
  PAY_AT_PICKUP: "pay_at_pickup",
  ONLINE_UPFRONT: "online_upfront"
};
var MAX_TIP_MULTIPLIER = 2;
var RIDE_TYPES = {
  POINT_TO_POINT: "point-to-point",
  HOURLY: "hourly",
  CUSTOM: "custom"
};
var RIDE_SUB_TYPES = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure"
};
var DOCUMENT_TYPES = {
  DRIVER_LICENSE: "driver_license",
  INSURANCE: "insurance",
  VEHICLE_REGISTRATION: "vehicle_registration",
  OTHER: "other"
};
var DOCUMENT_ACCESS_STATUS = {
  PENDING: "pending",
  GRANTED: "granted",
  DENIED: "denied",
  REVOKED: "revoked"
};
var DOCUMENT_REVIEW_STATUS = {
  APPROVED: "approved",
  REJECTED: "rejected"
};
var DRIVER_APPROVAL_STATUS = {
  PENDING_DOCUMENTS: "pending_documents",
  DOCUMENTS_RECEIVED: "documents_received",
  APPROVED: "approved",
  REJECTED: "rejected",
  MORE_INFO_NEEDED: "more_info_needed"
};
var PLATFORM_APPROVAL_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected"
};
var EMAIL_CONFIRMATION_CHANNEL = {
  MAILBOX: "mailbox",
  SMS_CARRYOVER: "sms_carryover"
};
var DRIVER_REJECTION_REASONS = {
  INCOMPLETE_APPLICATION: "incomplete_application",
  SUSPICIOUS_ACTIVITY: "suspicious_activity",
  DUPLICATE_APPLICATION: "duplicate_application",
  SERVICE_AREA_UNAVAILABLE: "service_area_unavailable",
  OTHER: "other"
};
var DRIVER_REJECTION_REASON_LABELS = {
  [DRIVER_REJECTION_REASONS.INCOMPLETE_APPLICATION]: "Incomplete application information",
  [DRIVER_REJECTION_REASONS.SUSPICIOUS_ACTIVITY]: "Suspicious account activity detected",
  [DRIVER_REJECTION_REASONS.DUPLICATE_APPLICATION]: "Duplicate application detected",
  [DRIVER_REJECTION_REASONS.SERVICE_AREA_UNAVAILABLE]: "Service not available in your area",
  [DRIVER_REJECTION_REASONS.OTHER]: "Other (see notes)"
};
var REGISTRATION_TYPES = {
  CUSTOMER: "customer",
  // No approval needed
  DRIVER: "driver",
  // Platform approval required
  COMPANY_ADMIN: "company_admin"
  // Platform approval required
};
var FLEET_SIZES = {
  ONE: "1",
  TWO_TO_FIVE: "2-5",
  SIX_TO_TEN: "6-10",
  ELEVEN_TO_TWENTYFIVE: "11-25",
  TWENTYSIX_PLUS: "26+"
};
var INVITATION_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  EXPIRED: "expired",
  CANCELLED: "cancelled"
};
var FLIGHT_STATUS = {
  SCHEDULED: "scheduled",
  DELAYED: "delayed",
  DEPARTED: "departed",
  ARRIVED: "arrived",
  CANCELLED: "cancelled"
};
var AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
  BUSY: "busy"
};
var COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "AF", name: "Afghanistan" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brazil" },
  { code: "BN", name: "Brunei" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cabo Verde" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo (Democratic Republic)" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "C\xF4te d'Ivoire" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GR", name: "Greece" },
  { code: "GD", name: "Grenada" },
  { code: "GT", name: "Guatemala" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HN", name: "Honduras" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KP", name: "Korea (North)" },
  { code: "KR", name: "Korea (South)" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "MK", name: "North Macedonia" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "QA", name: "Qatar" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VA", name: "Vatican City" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" }
];
var ERROR_CODES = {
  // Authentication
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  ACCOUNT_TYPE_CONFLICT: "ACCOUNT_TYPE_CONFLICT",
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
  // Authorization
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  COMPANY_CONTEXT_MISSING: "COMPANY_CONTEXT_MISSING",
  ROLE_REQUIRED: "ROLE_REQUIRED",
  // Resources
  RESOURCE_NOT_FOUND: "RESOURCE_NOT_FOUND",
  RESOURCE_ALREADY_EXISTS: "RESOURCE_ALREADY_EXISTS",
  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_STATE_TRANSITION: "INVALID_STATE_TRANSITION",
  LATE_CANCEL_ACKNOWLEDGEMENT_REQUIRED: "LATE_CANCEL_ACKNOWLEDGEMENT_REQUIRED",
  /**
   * An hourly booking arrived without a duration, or with one shorter than the
   * service level's configured minimum. Distinguished from the generic
   * validation code because it is the one refusal a client can act on
   * mechanically: the response's `details.minimumHours` is both the floor and
   * the value to prefill. A client that does not recognize the code still gets
   * a 400 with a human-readable message.
   */
  HOURLY_DURATION_REQUIRED: "HOURLY_DURATION_REQUIRED",
  // Subscription
  FEATURE_NOT_AVAILABLE: "FEATURE_NOT_AVAILABLE",
  LIMIT_EXCEEDED: "LIMIT_EXCEEDED",
  SUBSCRIPTION_REQUIRED: "SUBSCRIPTION_REQUIRED",
  BILLING_NOT_CONFIGURED: "BILLING_NOT_CONFIGURED",
  // Rate Limiting
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  // Server
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  // Step-up re-authentication (auth-hardening epic #497, Phase A — #498)
  STEP_UP_REQUIRED: "STEP_UP_REQUIRED"
};
var CONFLICT_REASONS = {
  /**
   * The write lost an optimistic-concurrency check: the reservation changed
   * (a different version, a different assignment generation, or an
   * `expectedUpdatedAt` that no longer matches) between read and write. The
   * client's remedy is to reload and reapply, never to retry blindly.
   */
  RESERVATION_VERSION_CONFLICT: "reservation_version_conflict",
  /**
   * A reservation capability-token claim targeted a reservation that already
   * belongs to a different rider. Clients must leave ownership unchanged and
   * must not infer or display any information about the current owner.
   */
  RESERVATION_ALREADY_LINKED: "reservation_already_linked",
  /**
   * The authenticated rider owns the reservation, but that ownership was not
   * created by passive verified-contact linking. Only passive links can be
   * disowned; explicit token claims and ordinary signed-in bookings are final.
   */
  RESERVATION_NOT_DISOWNABLE: "reservation_not_disownable",
  /**
   * A manual driver assignment found an overlapping ride under the commit-time
   * driver lock. The client may retry only after an operator explicitly
   * confirms that one ride overlap; personal time off is never covered.
   */
  DRIVER_BUSY_CONFIRM_REQUIRED: "driver_busy_confirm_required",
  /**
   * Registration completion (`POST /auth/register`) was aimed at an account
   * that already exists — an established customer/operator asked to be turned
   * into a driver by re-running signup. The endpoint refuses rather than
   * overwrite the account's name, attestations, or approval state. The client's
   * remedy is to route the (already-authenticated) owner to sign-in and let them
   * apply as a driver from their account (`POST /users/me/driver-application`),
   * never to retry registration.
   */
  ACCOUNT_ALREADY_REGISTERED: "account_already_registered"
};
var HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  // Used by the step-up re-auth gate (#498): the client must present a
  // recent password re-authentication before the request can proceed.
  PRECONDITION_REQUIRED: 428,
  TOO_MANY_REQUESTS: 429,
  // Request body exceeds a route's declared bound (rejected on the declared
  // Content-Length before any parse/validation work).
  PAYLOAD_TOO_LARGE: 413,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
};
var STEP_UP_RECENCY_MINUTES = 15;
var INQUIRY_STATUS = {
  NEW: "new",
  CONTACTED: "contacted",
  CONVERTED: "converted",
  CLOSED: "closed"
};
var DEFAULT_BRANDING_PRIMARY_COLOR = "#18794B";
var KNOWN_DEFAULT_BRANDING_COLORS = [
  DEFAULT_BRANDING_PRIMARY_COLOR,
  // Retired bootstrap blue — the business onboarding path's original default.
  "#007bff",
  // Retired solo blue — what the solo-driver and driver-plan provisioning
  // paths wrote before they were routed through the canonical default.
  "#2563eb"
];
var DEFAULT_BRANDING_COLOR_SET = new Set(
  KNOWN_DEFAULT_BRANDING_COLORS.map((hex) => hex.toLowerCase())
);
function isDefaultBrandingColor(hex) {
  return DEFAULT_BRANDING_COLOR_SET.has(hex.toLowerCase());
}
var SURCHARGE_RULE_TYPES = ["airport", "late_night", "holiday", "per_stop"];
var INQUIRY_PLAN_OPTIONS = {
  BUSINESS: "business",
  BUSINESS_PLUS: "business_plus",
  BUSINESS_ULTIMATE: "business_ultimate",
  HELP_ME_CHOOSE: "help_me_choose"
};
var INQUIRY_SOURCE = {
  REGISTRATION: "registration_inquiry",
  HOMEPAGE_DEMO: "homepage_demo"
};
var RESERVATION_USAGE_PLAN_COPY = {
  free_driver: "10 created reservation legs per UTC month (hard cap)",
  paid_driver: "75 completed reservation legs included per active usage period, then metered overage at $1.00 per additional completed leg",
  business: "150 completed reservation legs included per active usage period, then metered overage at $0.50 per additional completed leg",
  business_plus: "1,000 completed reservation legs included per active usage period, then metered overage at $0.25 per additional completed leg",
  business_ultimate: "Unlimited included completed reservation legs"
};
var COMPANY_PLAN_DETAILS = {
  business: {
    id: "business",
    name: "Business",
    price: 99,
    maxDrivers: 10,
    reservationsPerMonth: 150,
    features: [
      RESERVATION_USAGE_PLAN_COPY.business,
      "Up to 10 drivers",
      "Driver management & assignment",
      "Basic analytics",
      "Custom branding",
      "SMS notifications (200/month included)",
      // #1935: surcharge authoring starts at Business, where company
      // admin/manager roles can reach the configuration surface.
      "Surcharge policies (airport, late-night, holiday)",
      // #1703 (pricing epic #1702 WS-A): flight tracking is now built into
      // every tier — the add-on SKU is retired.
      "Flight tracking included",
      "Email support"
    ]
  },
  business_plus: {
    id: "business_plus",
    name: "Business Plus",
    price: 199,
    maxDrivers: 25,
    reservationsPerMonth: 1e3,
    features: [
      RESERVATION_USAGE_PLAN_COPY.business_plus,
      "Up to 25 drivers",
      "Embeddable booking widget",
      "SMS notifications (500/month included)",
      // Business Plus has always had surcharge policies (uncapped); this
      // bullet was a pre-existing marketing gap surfaced while adding #1031's
      // headline-entitlement completeness check.
      "Surcharge policies (airport, late-night, holiday)",
      // #1031: NEW capabilities at Business Plus — percent-type surcharge
      // rules (sophistication) + capped geographic zone pricing (scale).
      "Percent-based surcharge rules",
      "Up to 25 pricing zones",
      // #1703 (pricing epic #1702 WS-A): flight tracking is now built into
      // every tier — the add-on SKU is retired. Recorded consequence:
      // Business Plus now reaches monitored airports without an add-on
      // purchase (maxMonitoredAirports stays 4, unchanged).
      "Flight tracking included",
      "Priority support"
    ]
  },
  business_ultimate: {
    id: "business_ultimate",
    name: "Business Ultimate",
    price: 499,
    maxDrivers: null,
    // unlimited
    reservationsPerMonth: null,
    // unlimited
    features: [
      RESERVATION_USAGE_PLAN_COPY.business_ultimate,
      "Unlimited drivers",
      "Flight tracking included",
      "AI auto-dispatch",
      "SMS notifications (2,000/month included)",
      // Business Ultimate has always had surcharge policies (uncapped); this
      // bullet was a pre-existing marketing gap surfaced while adding #1031's
      // headline-entitlement completeness check.
      "Surcharge policies (airport, late-night, holiday)",
      // #1031: percent-based surcharge rules + unlimited zone pricing.
      "Percent-based surcharge rules",
      "Unlimited pricing zones",
      "Dedicated account manager & 24/7 support"
    ]
  }
};
var DRIVER_PLAN_DETAILS = {
  free_driver: {
    id: "free_driver",
    name: "Free",
    price: 0,
    reservationsPerMonth: 10,
    features: [
      RESERVATION_USAGE_PLAN_COPY.free_driver,
      "Basic booking page",
      "Email notifications",
      "Mobile app access",
      // #1703 (pricing epic #1702 WS-A, JP decision 2026-07-19): flight
      // tracking is built into EVERY tier, including Free — the add-on SKU
      // is retired. Not claim-checked against DRIVER_PLAN_DETAILS in
      // planConstantsDrift.test.ts (only COMPANY_PLAN_DETAILS bullets are
      // positively drift-tested; driver-plan bullets are audited separately)
      // but kept accurate against the fixture regardless.
      "Flight tracking included"
    ]
  },
  paid_driver: {
    id: "paid_driver",
    name: "Professional",
    price: 29,
    reservationsPerMonth: 75,
    // NOTE: no "SMS notifications" bullet — the paid_driver tier does NOT
    // include notification SMS (allowSmsNotifications=false; SMS starts at
    // Business). Drift-tested in apps/api/tests/config/planConstantsDrift.test.ts.
    features: [
      RESERVATION_USAGE_PLAN_COPY.paid_driver,
      "Custom booking page",
      "Customer management",
      "Online card payments (Stripe)",
      "Payment tracking",
      // Distance/time pricing moved DOWN to paid_driver (#1031, pricing epic
      // #1025 U6, monotonic tier ladder) — a solo-pro table stake.
      "Distance & time-based pricing",
      // #1703: flight tracking is built into every tier — the add-on SKU is
      // retired (this tier used to be eligible to purchase it).
      "Flight tracking included",
      "Priority support"
    ]
  }
};
var PROFESSIONAL_UPGRADE_FEATURES = [
  RESERVATION_USAGE_PLAN_COPY.paid_driver,
  "Custom booking page",
  "Customer management",
  "Online card payments (Stripe)"
];
var BUSINESS_START_FEATURES = [
  "Up to 10 drivers",
  RESERVATION_USAGE_PLAN_COPY.business,
  "Driver assignment and team management",
  "Basic company analytics",
  "Basic branding with logo and color",
  "SMS notifications and distance/time pricing"
];
var CSV_EXPORT_MAX_ROWS = 5e3;
var FLEET_SORT_OPTIONS = [
  { id: "default", column: null, direction: null, label: "Default first" },
  { id: "make-asc", column: "make", direction: "asc", label: "Make (A\u2013Z)" },
  { id: "make-desc", column: "make", direction: "desc", label: "Make (Z\u2013A)" },
  { id: "model-asc", column: "model", direction: "asc", label: "Model (A\u2013Z)" },
  { id: "year-desc", column: "year", direction: "desc", label: "Year (newest)" },
  { id: "year-asc", column: "year", direction: "asc", label: "Year (oldest)" },
  { id: "capacity-desc", column: "capacity", direction: "desc", label: "Seats (most)" },
  { id: "plate-asc", column: "licensePlate", direction: "asc", label: "Plate (A\u2013Z)" },
  { id: "added-desc", column: "createdAt", direction: "desc", label: "Recently added" }
];
var FLEET_STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "retired", label: "Retired" },
  { value: "all", label: "All" }
];

export { ADDRESS_SUGGESTION_TYPES, AVAILABILITY_STATUS, BUSINESS_START_FEATURES, COMPANY_DECLINED_CANCELLATION_REASON, COMPANY_PLAN_DETAILS, COMPANY_ROLES, COMPANY_STATUS, COMPLETION_OUTCOME, COMPLETION_OUTCOME_LABELS, CONFLICT_REASONS, COUNTRIES, CSV_EXPORT_MAX_ROWS, DEFAULT_BRANDING_PRIMARY_COLOR, DOCUMENT_ACCESS_STATUS, DOCUMENT_REVIEW_STATUS, DOCUMENT_TYPES, DRIVER_APPROVAL_STATUS, DRIVER_PLAN_DETAILS, DRIVER_REJECTION_REASONS, DRIVER_REJECTION_REASON_LABELS, EMAIL_CONFIRMATION_CHANNEL, ERROR_CODES, FLEET_SIZES, FLEET_SORT_OPTIONS, FLEET_STATUS_FILTERS, FLIGHT_STATUS, HTTP_STATUS, INQUIRY_PLAN_OPTIONS, INQUIRY_SOURCE, INQUIRY_STATUS, INVITATION_STATUS, KNOWN_DEFAULT_BRANDING_COLORS, MAX_TIP_MULTIPLIER, OVERDUE_ELIGIBLE_STATUSES, OVERDUE_ELIGIBLE_STATUS_SET, OVERDUE_SCOPE_STATUS_PARAM, PAYMENT_METHODS, PAYMENT_REQUIREMENT, PAYMENT_STATUS, PICKUP_MODE, PLACEHOLDER_DRIVER_SMS_CONSENT_VERSION, PLATFORM_ADMIN_ROLES, PLATFORM_APPROVAL_STATUS, PLATFORM_PERMISSIONS, PROFESSIONAL_UPGRADE_FEATURES, REGISTRATION_TYPES, RESERVATION_CANCELLATION_OUTCOME, RESERVATION_ISSUE_ACTOR_ROLE, RESERVATION_ISSUE_ACTOR_ROLES, RESERVATION_ISSUE_CATEGORIES, RESERVATION_ISSUE_CATEGORY, RESERVATION_ISSUE_EVENT_TYPE, RESERVATION_ISSUE_EVENT_TYPES, RESERVATION_ISSUE_IDEMPOTENCY_KEY_MAX_LENGTH, RESERVATION_ISSUE_OPERATION, RESERVATION_ISSUE_REPORTER_ROLE, RESERVATION_ISSUE_REPORTER_ROLES, RESERVATION_ISSUE_SEVERITIES, RESERVATION_ISSUE_SEVERITY, RESERVATION_ISSUE_STATUS, RESERVATION_ISSUE_STATUSES, RESERVATION_STAGE_LABELS, RESERVATION_STATUS, RESERVATION_STATUS_TRANSITIONS, RESERVATION_USAGE_PLAN_COPY, RIDE_SUB_TYPES, RIDE_TYPES, STEP_UP_RECENCY_MINUTES, SUBSCRIPTION_STATUS, SURCHARGE_RULE_TYPES, canTransitionReservation, getReservationStage, isDefaultBrandingColor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map